import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import type {
    InventoryMetricsSummaryRequest,
    InventoryMetricsSummaryResponse,
    ReportingComparison,
    InventoryReportingAdjustmentType,
} from '../../types/reporting';
import type { InventoryPartType } from '../../types/inventory';

export interface UseInventoryReportingSummaryOptions {
    locationIds?: string[];
    preset?: string;
    types?: InventoryReportingAdjustmentType[];
    tagIds?: string[];
    binIds?: string[];
    partTypes?: InventoryPartType[];
    supplierIds?: string[];
    search?: string;
    restockAlert?: boolean;
    comparison?: ReportingComparison;
    includeTagLocationBreakdown?: boolean;
    enabled?: boolean;
}

const getInventoryReportingSummary = async (
    sessionId: string,
    input: InventoryMetricsSummaryRequest,
): Promise<InventoryMetricsSummaryResponse> => {
    const { data } = await axios.post<InventoryMetricsSummaryResponse>(
        `${API_URL}/reporting/inventory/summary`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useInventoryReportingSummary = (
    startDate: string,
    endDate: string,
    options: UseInventoryReportingSummaryOptions = {},
) => {
    const sessionContext = useContext(SessionContext);
    const {
        locationIds,
        preset,
        types,
        tagIds,
        binIds,
        partTypes,
        supplierIds,
        search,
        restockAlert,
        comparison,
        includeTagLocationBreakdown,
        enabled = true,
    } = options;

    return useQuery({
        queryKey: [
            'reporting',
            'inventory',
            'summary',
            startDate,
            endDate,
            locationIds,
            preset,
            types,
            tagIds,
            binIds,
            partTypes,
            supplierIds,
            search,
            restockAlert,
            comparison,
            includeTagLocationBreakdown,
        ],
        queryFn: () =>
            getInventoryReportingSummary(sessionContext?.sessionId ?? '', {
                startDate,
                endDate,
                locationIds: locationIds?.length ? locationIds : undefined,
                preset,
                types: types?.length ? types : undefined,
                tagIds: tagIds?.length ? tagIds : undefined,
                binIds: binIds?.length ? binIds : undefined,
                partTypes: partTypes?.length ? partTypes : undefined,
                supplierIds: supplierIds?.length ? supplierIds : undefined,
                search: search || undefined,
                restockAlert,
                includeTagLocationBreakdown,
                // When comparing: send a valid comparison. Otherwise omit / false (e.g. custom ranges).
                ...(comparison
                    ? { includeComparison: true as const, comparison }
                    : { includeComparison: false as const }),
            }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
